import type { ReactNode } from 'react'

import type { Api, Page, Router } from '@subaio/utils'
import type { Container as ContainerBase, Params } from '@subaio/webapp/src/containers'

import { LoadingScreen } from 'components/LoadingScreen'

import type { UserAuth } from './store'
import type { getUserAuth } from './userAuth'

export type Extensions = {
	path?: string
	hideHeaderAndFooter?: boolean
	authentication: /** Can always be accessed */
		| { type: 'public' }
		/** Can only be accessed with a valid token */
		| { type: 'private' }
}

export type Props<TState extends Record<string, unknown>, TDependencies extends keyof TState> = {
	query: Page['query']
	api: Api
	locale: string
	userAuth: UserAuth
	invalidateSelf: (...dependencies: Array<TDependencies | string>) => Promise<void>
	setData: (update: Partial<TState>) => void
	setUser: (user: Parameters<typeof getUserAuth>[0]) => void
	setPageTitle: (title: ReactNode) => void
	setLocale: (locale: string) => Promise<void>
}

export type InitProps = {
	query: Page['query']
	router: Router
	api: Api
	locale: string
	userAuth: UserAuth
	updatePage: (page: Page) => void
	setUser: (options?: { token: string }) => void
	setPageTitle: (title: ReactNode) => void
	setLocale: (locale: string) => Promise<void>
}

export type Container<
	TState extends Record<string, unknown> = Record<string, any>,
	TParams extends Params = Record<string, any>,
	TDefaults extends keyof TState = keyof TState,
	TDependencies extends keyof TState = keyof TState,
> = ContainerBase<Extensions, Props<TState, TDependencies>, InitProps, TState, TParams, TDefaults, TDependencies>

export const asContainer =
	<TState extends Record<string, unknown> = {}, TParams extends Params = {}>() =>
	<TDefaults extends keyof TState = never, TDependencies extends keyof TState = never>(
		container: Omit<
			ContainerBase<Extensions, Props<TState, TDependencies>, InitProps, TState, TParams, TDefaults, TDependencies>,
			'Loading'
		>,
	) =>
		({
			...container,
			Loading: () => <LoadingScreen />,
		}) as ContainerBase<Extensions, Props<TState, TDependencies>, InitProps, TState, TParams, TDefaults, TDependencies>
